import React, { useState } from 'react';
import {
  Button,
  FormHelperText,
  Grid,
  Link,
  IconButton,
  InputAdornment,
  InputLabel,
  OutlinedInput,
  Stack,
  Alert,
  Snackbar,
  Typography
} from '@mui/material';
import * as Yup from 'yup';
import { Formik } from 'formik';
import { EyeOutlined, EyeInvisibleOutlined } from '@ant-design/icons';
import AnimateButton from 'components/@extended/AnimateButton';
import { useNavigate, useParams } from 'react-router-dom';
import { changeUserPassword } from 'services/apiService';
import useToken from 'services/useToken';

const ChangePasswordForm = (props) => {
  const { organizationName } = useParams();
  const [showOldPassword, setShowOldPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const navigate = useNavigate();
  const { token } = useToken();
  const [open, setOpen] = useState(false);
  const [alertMessage, setMessage] = useState('');
  const [severity, setSeverity] = useState('success');
  const handleClose = () => {
    setOpen(false);
  };

  const navigateToHomeScreen = () => {
    navigate(`${organizationName}/device-info`);
  };

  const handleClickShowOldPassword = () => {
    setShowOldPassword(!showOldPassword);
  };

  const handleClickShowNewPassword = () => {
    setShowNewPassword(!showNewPassword);
  };

  const handleClickShowConfirmPassword = () => {
    setShowConfirmPassword(!showConfirmPassword);
  };

  const handleMouseDownPassword = (event) => {
    event.preventDefault();
  };

  const changePassword = async (data, token) => {
    const response = await changeUserPassword(data, token);
    return response.data;
  };

  return (
    <>
      <Formik
        initialValues={{
          oldPassword: '',
          newPassword: '',
          confirmPassword: '',
          submit: null
        }}
        validationSchema={Yup.object().shape({
          oldPassword: Yup.string().required('Old password is required'),
          newPassword: Yup.string().required('New password is required').min(8, 'Password must be at least 8 characters'),
          confirmPassword: Yup.string()
            .required('Confirm password is required')
            .oneOf([Yup.ref('newPassword'), null], 'Passwords must match')
        })}
        onSubmit={async (values, { setErrors, setStatus, setSubmitting, resetForm }) => {
          const oldPassword = values.oldPassword;
          const newPassword = values.newPassword;
          const confirmPassword = values.confirmPassword;

          try {
            if (newPassword === confirmPassword) {
              const response = await changePassword(
                { old_password: oldPassword, new_password: newPassword, confirm_new_password: confirmPassword },
                token
              );

              if (response.status === 200 && !response.error) {
                setMessage(response?.message);
                setSeverity('success');
                setOpen(true);
                resetForm();
                setTimeout(() => {
                  navigate(`/${organizationName}/home`);
                }, 1500);
              } else if (response.status !== 200 || response.error) {
                if (response?.message === 'Old password is incorrect') {
                  setErrors({ oldPassword: 'Old password is incorrect' });
                } else {
                  setMessage(response?.message);
                  setSeverity('error');
                  setOpen(true);
                }
              }
            }
          } catch (error) {
            console.log('error', error);
            setMessage('An unexpected error occurred');
            setSeverity('error');
            setOpen(true);
          }
          setSubmitting(false);
        }}
      >
        {({ errors, handleBlur, handleChange, handleSubmit, isSubmitting, touched, values }) => (
          <form noValidate onSubmit={handleSubmit}>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Stack spacing={1}>
                  <InputLabel htmlFor="new-password">Old Password</InputLabel>
                  <OutlinedInput
                    id="old-password"
                    type={showOldPassword ? 'text' : 'password'}
                    value={values.oldPassword}
                    name="oldPassword"
                    onBlur={handleBlur}
                    onChange={handleChange}
                    fullWidth
                    error={Boolean(touched.oldPassword && errors.oldPassword)}
                    endAdornment={
                      <InputAdornment position="end">
                        <IconButton
                          aria-label="toggle old password visibility"
                          onClick={handleClickShowOldPassword}
                          onMouseDown={handleMouseDownPassword}
                          edge="end"
                          size="large"
                        >
                          {showOldPassword ? <EyeOutlined /> : <EyeInvisibleOutlined />}
                        </IconButton>
                      </InputAdornment>
                    }
                    placeholder="Enter old password"
                  />
                  {touched.oldPassword && errors.oldPassword && <FormHelperText error>{errors.oldPassword}</FormHelperText>}
                </Stack>
              </Grid>
              <Grid item xs={12}>
                <Stack spacing={1}>
                  <InputLabel htmlFor="new-password">New Password</InputLabel>
                  <OutlinedInput
                    id="new-password"
                    type={showNewPassword ? 'text' : 'password'}
                    value={values.newPassword}
                    name="newPassword"
                    onBlur={handleBlur}
                    onChange={handleChange}
                    fullWidth
                    error={Boolean(touched.newPassword && errors.newPassword)}
                    endAdornment={
                      <InputAdornment position="end">
                        <IconButton
                          aria-label="toggle new password visibility"
                          onClick={handleClickShowNewPassword}
                          onMouseDown={handleMouseDownPassword}
                          edge="end"
                          size="large"
                        >
                          {showNewPassword ? <EyeOutlined /> : <EyeInvisibleOutlined />}
                        </IconButton>
                      </InputAdornment>
                    }
                    placeholder="Enter new password"
                  />
                  {touched.newPassword && errors.newPassword && <FormHelperText error>{errors.newPassword}</FormHelperText>}
                </Stack>
              </Grid>
              <Grid item xs={12}>
                <Stack spacing={1}>
                  <InputLabel htmlFor="confirm-password">Confirm Password</InputLabel>
                  <OutlinedInput
                    id="confirm-password"
                    type={showConfirmPassword ? 'text' : 'password'}
                    value={values.confirmPassword}
                    name="confirmPassword"
                    onBlur={handleBlur}
                    onChange={handleChange}
                    fullWidth
                    error={Boolean(touched.confirmPassword && errors.confirmPassword)}
                    endAdornment={
                      <InputAdornment position="end">
                        <IconButton
                          aria-label="toggle confirm password visibility"
                          onClick={handleClickShowConfirmPassword}
                          onMouseDown={handleMouseDownPassword}
                          edge="end"
                          size="large"
                        >
                          {showConfirmPassword ? <EyeOutlined /> : <EyeInvisibleOutlined />}
                        </IconButton>
                      </InputAdornment>
                    }
                    placeholder="Confirm new password"
                  />
                  {touched.confirmPassword && errors.confirmPassword && <FormHelperText error>{errors.confirmPassword}</FormHelperText>}
                </Stack>
              </Grid>
              <Grid item xs={12}>
                {errors.submit && <FormHelperText error>{errors.submit}</FormHelperText>}
              </Grid>
              <Grid item xs={12}>
                <AnimateButton>
                  <Button disableElevation disabled={isSubmitting} fullWidth size="large" type="submit" variant="contained" color="primary">
                    Change Password
                  </Button>
                </AnimateButton>
                <div style={{ marginTop: 8, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                  <Button color="secondary" variant="outlined" onClick={navigateToHomeScreen}>
                    Go to Home
                  </Button>
                </div>
              </Grid>
            </Grid>
          </form>
        )}
      </Formik>
      <Snackbar open={open} autoHideDuration={6000} onClose={handleClose}>
        <Alert onClose={handleClose} severity={severity} sx={{ width: '100%' }}>
          {alertMessage}
        </Alert>
      </Snackbar>
    </>
  );
};

export default ChangePasswordForm;
